import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import MultiSelect from '../common/MultiSelect';
import { toast } from 'react-hot-toast';
import { ShareService } from '../../services/share.service';
import { TeamService } from '../../services/team.service';
import { ItemTitle, SelectedItem, GroupedItems } from '../../types/share.types';
import { Team } from '../../types/team.types';
import { XMarkIcon, AdjustmentsHorizontalIcon, ShareIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from '../../hooks/useRedux';
import { hasPermission } from '../../utils/permissionUtils';
import { useWorkspace } from '../../contexts/WorkspaceContext';

interface ShareItemsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const ShareItemsModal: React.FC<ShareItemsModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { team_id, permissions } = useAppSelector((state) => state.auth);
    const [shareType, setShareType] = useState<'individual' | 'team'>('individual');
    const [email, setEmail] = useState('');
    const [permissionLevel, setPermissionLevel] = useState<'read' | 'write' | 'manage'>('read');
    const [expiresIn, setExpiresIn] = useState('never');
    const [isLoading, setIsLoading] = useState(false);
    const [items, setItems] = useState<GroupedItems>({
        passwords: [],
        notes: [],
        cards: [],
        banks: [],
        personal: [],
        categories: []
    });
    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [selectedItemTypes, setSelectedItemTypes] = useState({
        passwords: true,
        notes: true,
        cards: true,
        banks: true,
        personal: true,
        categories: true
    });
    const { role_id } = useAppSelector((state) => state.auth);
    const [showFilters, setShowFilters] = useState(false);
    const canShareWithTeam = hasPermission(permissions, 'vault', 'vault_share_teams');
    const [organizations, setOrganizations] = useState<Team[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState<string>('');
    const {activeWorkspace} = useWorkspace();

    // Fetch all items when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchItems();
        }
    }, [isOpen]);

    // Add useEffect for fetching teams
    useEffect(() => {
        if (isOpen && canShareWithTeam && shareType === 'team') {
            fetchTeams();
        }
    }, [isOpen, canShareWithTeam, shareType]);

    const fetchItems = async () => {
        setIsLoadingItems(true);
        try {
            const data = await ShareService.getAllItemTitles(activeWorkspace?.id || '');
            console.log("all data", data);
            setItems(data);
        } catch (error) {
            toast.error('Failed to fetch items');
            console.error('Error fetching items:', error);
        } finally {
            setIsLoadingItems(false);
        }
    };

    const fetchTeams = async () => {
        try {
            const response = await TeamService.getUserTeams();
            // Filter teams (teams with no parent)
            const teams = response.teams?.filter(team => 
                team.team_type === "department" || !team.parent_team_id
            ) || [];
            setOrganizations(teams as Team[]);
            
            // Set default team if available
            if (teams.length > 0) {
                setSelectedTeamId(teams[0].id);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
            toast.error('Failed to load organizations');
        }
    };

    const handleItemSelect = (item: ItemTitle) => {
        // Check if item is already selected
        if (!selectedItems.some(selected => selected.id === item.id && selected.type === item.type)) {
            setSelectedItems([...selectedItems, { ...item, selected: true }]);
        }
    };

    const handleItemRemove = (item: ItemTitle) => {
        setSelectedItems(selectedItems.filter(
            selected => !(selected.id === item.id && selected.type === item.type)
        ));
    };

    const handleItemTypeToggle = (type: keyof typeof selectedItemTypes) => {
        setSelectedItemTypes(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    const getFilteredItems = (): GroupedItems => {
        const filteredItems: GroupedItems = {
            passwords: [],
            notes: [],
            cards: [],
            banks: [],
            personal: [],
            categories: []
        };

        if (selectedItemTypes.passwords) filteredItems.passwords = items.passwords;
        if (selectedItemTypes.notes) filteredItems.notes = items.notes;
        if (selectedItemTypes.cards) filteredItems.cards = items.cards;
        if (selectedItemTypes.banks) filteredItems.banks = items.banks;
        if (selectedItemTypes.personal) filteredItems.personal = items.personal;
        if (role_id !== 3) {
                if (selectedItemTypes.categories) filteredItems.categories = items.categories;
        }

        return filteredItems;
    };

    const handleShare = async (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedItems.length === 0) {
            toast.error('Please select at least one item to share');
            return;
        }

        if (shareType === 'individual' && !email) {
            toast.error('Please enter an email address');
            return;
        }

        if (shareType === 'team' && canShareWithTeam && !selectedTeamId) {
            toast.error('Please select a team');
            return;
        }

        setIsLoading(true);

        try {
            const expiresAt = expiresIn === 'never'
                ? undefined
                : new Date(Date.now() + parseInt(expiresIn) * 24 * 60 * 60 * 1000);

            const itemIds = selectedItems.map(item => ({
                id: item.id,
                type: item.type
            }));

            // Determine which team ID to use based on user role and selection
            let shareTeamId = '';
            if (shareType === 'team') {
                if (canShareWithTeam) {
                    shareTeamId = selectedTeamId;
                } else {
                    // For regular users, use their department ID
                    shareTeamId = team_id || '';
                }
            }

            const response = await ShareService.shareItems({
                itemIds,
                sharedWithEmail: email,
                shareType,
                permissionLevel,
                expiresAt,
                sharedKey: '',
                teamId: shareTeamId,
                workspaceId: activeWorkspace?.id || team_id || ''
            });

            if(response[0].success) {
                toast.success('Items shared successfully');
                // Reset form state
                setSelectedItems([]);
                setEmail('');
                setPermissionLevel('read');
                setExpiresIn('never');
                setSelectedTeamId('');
                // Call onSuccess callback
                onSuccess?.();
                onClose();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to share items';
            toast.error(errorMessage);
            console.error('Share error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`fixed inset-0 z-50 overflow-hidden ${isOpen ? 'block' : 'hidden'}`}>
            {/* Backdrop with blur effect */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"></div>

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                    {/* Modern Header with Gradient */}
                    <div className="relative bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 text-white">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                    <ShareIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold">Share Items</h3>
                                    <p className="text-blue-100 text-sm">Share your vault items securely with team members</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                            >
                                <XMarkIcon className="h-6 w-6 text-white" />
                            </button>
                        </div>
                        
                        {/* Progress indicator */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                            <div className={`h-full transition-all duration-500 ${selectedItems.length > 0 ? 'w-1/2' : 'w-1/4'}`}></div>
                        </div>
                    </div>
                    
                    {/* Main Content with Modern Layout */}
                    <form onSubmit={handleShare} className="flex-1 overflow-auto">
                        <div className="p-6 space-y-8">
                            {/* Share Configuration Card */}
                            <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                                <div className="flex items-center mb-4">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg mr-3">
                                        <AdjustmentsHorizontalIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Share Configuration</h4>
                                </div>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Left Column - Share Type & Recipient */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                                Share with
                                            </label>
                                            <div className="flex space-x-4">
                                                <label className="flex items-center p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 cursor-pointer transition-all duration-200 flex-1">
                                                    <input
                                                        type="radio"
                                                        className="form-radio h-4 w-4 text-blue-600"
                                                        value="individual"
                                                        checked={shareType === 'individual'}
                                                        onChange={() => setShareType('individual')}
                                                    />
                                                    <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Individual</span>
                                                </label>
                                                {(team_id && canShareWithTeam) && (
                                                    <label className="flex items-center p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 cursor-pointer transition-all duration-200 flex-1">
                                                        <input
                                                            type="radio"
                                                            className="form-radio h-4 w-4 text-blue-600"
                                                            value="team"
                                                            checked={shareType === 'team'}
                                                            onChange={() => setShareType('team')}
                                                        />
                                                        <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Team</span>
                                                    </label>
                                                )}
                                            </div>
                                        </div>

                                        {/* Recipient Selection */}
                                        {shareType === 'individual' ? (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Recipient Email
                                                </label>
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-all duration-200"
                                                    placeholder="Enter email address..."
                                                    required
                                                />
                                            </div>
                                        ) : (
                                            canShareWithTeam && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Select Team
                                                    </label>
                                                    <select
                                                        value={selectedTeamId}
                                                        onChange={(e) => setSelectedTeamId(e.target.value)}
                                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-all duration-200"
                                                        required
                                                    >
                                                        <option value="">Select Team</option>
                                                        {organizations.map((org) => (
                                                            <option key={org.id} value={org.id}>
                                                                {org.name}
                                                            </option>
                                                        ))}
                                                        <option value={`${team_id}`}>Share With All Members</option>
                                                    </select>
                                                </div>
                                            )
                                        )}
                                    </div>

                                    {/* Right Column - Permissions & Expiry */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Permission Level
                                            </label>
                                            <select
                                                value={permissionLevel}
                                                onChange={(e) => setPermissionLevel(e.target.value as 'read' | 'write' | 'manage')}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-all duration-200"
                                            >
                                                <option value="read">👁️ View Only</option>
                                                <option value="write">✏️ View & Edit</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Expires In
                                            </label>
                                            <select
                                                value={expiresIn}
                                                onChange={(e) => setExpiresIn(e.target.value)}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-all duration-200"
                                            >
                                                <option value="never">♾️ Never</option>
                                                <option value="1">⏰ 24 hours</option>
                                                <option value="7">📅 7 days</option>
                                                <option value="30">📆 30 days</option>
                                                <option value="90">🗓️ 90 days</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Item Selection Card */}
                            <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow duration-200">
                                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
                                    <div className="flex items-center">
                                        <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg mr-3">
                                            <ShareIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Select Items to Share</h4>
                                            {selectedItems.length > 0 && (
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={() => setShowFilters(!showFilters)} 
                                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                                            showFilters 
                                                ? 'bg-blue-600 text-white shadow-lg' 
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                                    </button>
                                </div>
                                
                                {/* Enhanced Filters Section */}
                                {showFilters && (
                                    <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b border-gray-200 dark:border-gray-600">
                                        <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                                            <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                                            Filter by Item Type
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                                            {[
                                                { key: 'passwords', label: 'Passwords', icon: '🔑' },
                                                { key: 'cards', label: 'Cards', icon: '💳' },
                                                { key: 'banks', label: 'Banks', icon: '🏦' },
                                                { key: 'personal', label: 'Personal', icon: '👤' },
                                                ...(role_id !== 3 ? [{ key: 'categories', label: 'Vault', icon: '📁' }] : [])
                                            ].map((item) => (
                                                <label key={item.key} className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                                                    selectedItemTypes[item.key as keyof typeof selectedItemTypes]
                                                        ? 'bg-blue-100 dark:bg-blue-900/50 border-2 border-blue-500 shadow-md'
                                                        : 'bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                                                }`}>
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 focus:ring-2"
                                                        checked={selectedItemTypes[item.key as keyof typeof selectedItemTypes]}
                                                        onChange={() => handleItemTypeToggle(item.key as keyof typeof selectedItemTypes)}
                                                    />
                                                    <span className="ml-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                                                        {item.icon} {item.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Item Selection Grid */}
                                <div className="p-6">
                                    {isLoadingItems ? (
                                        <div className="flex flex-col justify-center items-center h-48">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-500 mb-4"></div>
                                            <p className="text-gray-600 dark:text-gray-400 font-medium">Loading your items...</p>
                                        </div>
                                    ) : (
                                        <MultiSelect
                                            items={getFilteredItems()}
                                            selectedItems={selectedItems}
                                            onItemSelect={handleItemSelect}
                                            onItemRemove={handleItemRemove}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Modern Action Bar */}
                        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    {selectedItems.length > 0 && (
                                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                            <span className="font-medium">
                                                {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} ready to share
                                            </span>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex space-x-3">
                                    <Button 
                                        type="button" 
                                        variant="secondary" 
                                        onClick={onClose}
                                        className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors duration-200"
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        loading={isLoading}
                                        disabled={selectedItems.length === 0 || (shareType === 'individual' && !email)}
                                        className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                                            selectedItems.length === 0 || (shareType === 'individual' && !email)
                                                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-primary-600 to-primary-900 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                                        }`}
                                    >
                                        {isLoading ? (
                                            <div className="flex items-center">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Sharing...
                                            </div>
                                        ) : (
                                            <>
                                                🚀 Share {selectedItems.length > 0 ? `(${selectedItems.length} items)` : ''}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ShareItemsModal; 